import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { authService } from '../services/authService';
import { t } from '../services/i18n';
import { formatDate, formatDateTime } from '../src/utils/i18nFormatters';
import { useLanguageUpdate } from '../src/hooks/useLanguageChange';
import { User as UserIcon, Mail, Phone, Globe, MapPin, CreditCard, Calendar, ShieldCheck, Edit, Save, X, History, Activity, BarChart3, CheckCircle, AlertCircle, Loader2, BookOpen, Zap, Rocket } from 'lucide-react';

interface ProfileProps {
  user: User;
  onUpdate?: (user: User) => void;
}

export const Profile: React.FC<ProfileProps> = ({ user, onUpdate }) => {
  // Listen for language changes to update translations
  useLanguageUpdate();
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState<Partial<User>>({
    name: user.name,
    email: user.email,
    whatsappNumber: user.whatsappNumber || '',
    preferredLanguage: user.preferredLanguage || 'English',
    preferredCountry: user.preferredCountry || 'United States',
    uiLanguage: user.uiLanguage || 'English',
    aiAssistantLanguage: user.aiAssistantLanguage || 'Darija/French'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showConsumption, setShowConsumption] = useState(false);
  const [consumption, setConsumption] = useState<any>(null);
  const [activity, setActivity] = useState<any[]>([]);
  const [loadingConsumption, setLoadingConsumption] = useState(false);
  const [showPaymentVerification, setShowPaymentVerification] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCreditDashboard, setShowCreditDashboard] = useState(false);
  const [creditUsage, setCreditUsage] = useState<any>(null);
  const [loadingCreditUsage, setLoadingCreditUsage] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    setMessage(null);
    try {
      // Update preferences
      if (editedUser.preferredLanguage && editedUser.preferredCountry) {
        const uiLanguage = user.uiLanguage || 'English';
        const aiAssistantLanguage = user.aiAssistantLanguage || 'Darija/French';
        await authService.updatePreferences(
          editedUser.preferredLanguage, 
          uiLanguage, 
          aiAssistantLanguage, 
          editedUser.preferredCountry
        );
      }

      // Update user info (if backend supports it)
      const updatedUser = { ...user, ...editedUser };
      if (onUpdate) {
        onUpdate(updatedUser as User);
      }

      setMessage({ type: 'success', text: t('profile.updateSuccess') || 'Profile updated successfully!' });
      setIsEditing(false);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || t('profile.updateFailed') || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedUser({
      name: user.name,
      email: user.email,
      whatsappNumber: user.whatsappNumber || '',
      preferredLanguage: user.preferredLanguage || 'English',
      preferredCountry: user.preferredCountry || 'United States',
      uiLanguage: user.uiLanguage || 'English',
      aiAssistantLanguage: user.aiAssistantLanguage || 'Darija/French'
    });
    setIsEditing(false);
    setMessage(null);
  };

  const formatDateLocal = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return formatDate(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleLoadConsumption = async () => {
    if (showConsumption) {
      setShowConsumption(false);
      return;
    }

    setLoadingConsumption(true);
    try {
      const [consumptionData, activityData] = await Promise.all([
        authService.getMyConsumption(),
        authService.getMyActivity()
      ]);
      
      // Map consumption data to expected format
      const mappedConsumption = {
        totalActions: (consumptionData.totalAudits || 0) + (consumptionData.totalKeywords || 0) + (consumptionData.totalCompetitors || 0),
        audits: consumptionData.totalAudits || 0,
        keywordSearches: consumptionData.totalKeywords || 0,
        competitorAnalyses: consumptionData.totalCompetitors || 0,
        creditsDeducted: consumptionData.totalCreditsUsed || 0,
        logins: consumptionData.totalLogins || 0,
        lastActivity: consumptionData.lastActivity
      };
      
      setConsumption(mappedConsumption);
      setActivity(activityData || []);
      setShowConsumption(true);
    } catch (error: any) {
      console.error('Failed to load consumption:', error);
      setMessage({ type: 'error', text: error.message || t('profile.loadConsumptionFailed') || 'Failed to load consumption data' });
    } finally {
      setLoadingConsumption(false);
    }
  };

  return (
    <div className="p-8 animate-fadeIn text-white">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold">{t('profile.title') || 'My Profile'}</h2>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors"
            >
              <Edit size={18} />
              {t('profile.editProfile') || 'Edit Profile'}
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg transition-colors disabled:opacity-50"
              >
                <Save size={18} />
                {t('common.save') || 'Save'}
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
              >
                <X size={18} />
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          )}
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
              : 'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            {message.text}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              <UserIcon className="text-indigo-400" size={24} />
              Personal Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Full Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedUser.name || ''}
                    onChange={(e) => setEditedUser({ ...editedUser, name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                ) : (
                  <div className="text-white font-medium">{user.name}</div>
                )}
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                  <Mail size={14} />
                  Email Address
                </label>
                <div className="text-white font-medium">{user.email}</div>
                <div className="text-xs text-slate-500 mt-1">Email cannot be changed</div>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                  <Phone size={14} />
                  WhatsApp Number
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={editedUser.whatsappNumber || ''}
                    onChange={(e) => setEditedUser({ ...editedUser, whatsappNumber: e.target.value })}
                    placeholder="+212 6XX XXX XXX"
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  />
                ) : (
                  <div className="text-white font-medium">{user.whatsappNumber || 'Not provided'}</div>
                )}
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              <ShieldCheck className="text-emerald-400" size={24} />
              Account Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Account Role</label>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded text-sm font-bold uppercase ${
                    user.role === 'admin' 
                      ? 'bg-indigo-500/20 text-indigo-400' 
                      : 'bg-slate-600/20 text-slate-400'
                  }`}>
                    {user.role}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                  <CreditCard size={14} />
                  Subscription Plan
                </label>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded text-sm font-bold uppercase ${
                    user.plan === 'free' 
                      ? 'bg-slate-600/20 text-slate-400' 
                      : 'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    {user.plan}
                  </span>
                </div>
                {user.subscriptionExpiry && (
                  <div className="text-xs text-slate-500 mt-1">
                    Expires: {formatDateLocal(user.subscriptionExpiry)}
                  </div>
                )}
                {/* Plan Credit Information */}
                <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <h4 className="text-sm font-semibold text-white mb-2">Credits per Plan</h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Free Plan:</span>
                      <span className="text-white font-medium">10 credits (one-time)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Weekly Plan:</span>
                      <span className="text-emerald-400 font-medium">Unlimited credits</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Monthly Plan:</span>
                      <span className="text-emerald-400 font-medium">Unlimited credits</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Yearly Plan:</span>
                      <span className="text-emerald-400 font-medium">Unlimited credits</span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                  <Activity size={14} />
                  Available Credits
                </label>
                <div className="flex items-center gap-2">
                  <span className={`text-2xl font-bold ${
                    user.plan !== 'free' && user.subscriptionExpiry && new Date(user.subscriptionExpiry) > new Date()
                      ? 'text-emerald-400'
                      : user.credits > 5
                      ? 'text-white'
                      : user.credits > 0
                      ? 'text-yellow-400'
                      : 'text-red-400'
                  }`}>
                    {user.plan !== 'free' && user.subscriptionExpiry && new Date(user.subscriptionExpiry) > new Date()
                      ? '∞'
                      : user.credits || 0}
                  </span>
                  {user.plan === 'free' && (
                    <span className="text-xs text-slate-500">
                      {user.credits <= 5 && user.credits > 0 && '(Low credits - consider upgrading)'}
                      {user.credits === 0 && '(No credits - upgrade required)'}
                    </span>
                  )}
                </div>
                {user.plan === 'free' && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <button
                      onClick={() => setShowPaymentVerification(!showPaymentVerification)}
                      className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-2"
                    >
                      <CreditCard size={14} />
                      Verify PayPal Payment
                    </button>
                    {showPaymentVerification && (
                      <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                        <p className="text-sm text-slate-300 mb-3">
                          If you paid via a PayPal payment link, enter your transaction ID to verify and activate your plan.
                        </p>
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={transactionId}
                            onChange={(e) => {
                              setTransactionId(e.target.value);
                              setPaymentMessage(null);
                            }}
                            placeholder="Enter PayPal Transaction ID"
                            className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2 text-sm"
                            disabled={verifyingPayment}
                          />
                          {paymentMessage && (
                            <div className={`p-3 rounded-lg flex items-start gap-2 ${
                              paymentMessage.type === 'success' 
                                ? 'bg-emerald-500/10 border border-emerald-500/30' 
                                : 'bg-red-500/10 border border-red-500/30'
                            }`}>
                              {paymentMessage.type === 'success' ? (
                                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                              ) : (
                                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                              )}
                              <p className={`text-sm ${
                                paymentMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'
                              }`}>
                                {paymentMessage.text}
                              </p>
                            </div>
                          )}
                          <button
                            onClick={async () => {
                              if (!transactionId.trim()) {
                                setPaymentMessage({ type: 'error', text: 'Please enter a transaction ID' });
                                return;
                              }
                              setVerifyingPayment(true);
                              setPaymentMessage(null);
                              try {
                                const updatedUser = await authService.verifyPayPalPayment(transactionId.trim());
                                setPaymentMessage({ 
                                  type: 'success', 
                                  text: `Payment verified! Your ${updatedUser.plan} plan is now active.` 
                                });
                                setTransactionId('');
                                if (onUpdate) {
                                  onUpdate(updatedUser);
                                }
                                // Reload user data
                                const currentUser = await authService.getCurrentUser();
                                if (currentUser && onUpdate) {
                                  onUpdate(currentUser);
                                }
                              } catch (error: any) {
                                setPaymentMessage({ 
                                  type: 'error', 
                                  text: error.message || 'Failed to verify payment. Please check your transaction ID or contact support.' 
                                });
                              } finally {
                                setVerifyingPayment(false);
                              }
                            }}
                            disabled={verifyingPayment || !transactionId.trim()}
                            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {verifyingPayment ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Verifying...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4" />
                                Verify Payment
                              </>
                            )}
                          </button>
                          <p className="text-xs text-slate-500 mt-2">
                            You can find your transaction ID in your PayPal receipt email or in your PayPal account activity.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Credits Remaining</label>
                <div className={`text-2xl font-bold ${
                  user.credits === 0 ? 'text-red-400' : user.credits < 5 ? 'text-yellow-400' : 'text-emerald-400'
                }`}>
                  {user.credits}
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                  <Calendar size={14} />
                  Member Since
                </label>
                <div className="text-white font-medium">{formatDateLocal(user.createdAt)}</div>
              </div>
            </div>
          </div>

          {/* Credit Usage Dashboard */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 md:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <BarChart3 className="text-indigo-400" size={24} />
                Credit Usage Dashboard
              </h3>
              <button
                onClick={async () => {
                  if (showCreditDashboard) {
                    setShowCreditDashboard(false);
                    return;
                  }
                  setLoadingCreditUsage(true);
                  try {
                    const usage = await authService.getCreditUsage();
                    // Ensure all properties are arrays/objects
                    const safeUsage = {
                      usageHistory: Array.isArray(usage.usageHistory) ? usage.usageHistory : [],
                      usageStats: Array.isArray(usage.usageStats) ? usage.usageStats : [],
                      totals: usage.totals || {
                        totalCreditsUsed: 0,
                        totalUsageCount: 0,
                        averageCreditsPerUse: '0'
                      },
                      toolCosts: Array.isArray(usage.toolCosts) ? usage.toolCosts : []
                    };
                    setCreditUsage(safeUsage);
                    setShowCreditDashboard(true);
                  } catch (error: any) {
                    setMessage({ type: 'error', text: error.message || 'Failed to load credit usage' });
                    setCreditUsage(null);
                  } finally {
                    setLoadingCreditUsage(false);
                  }
                }}
                disabled={loadingCreditUsage}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50"
              >
                {loadingCreditUsage ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Loading...
                  </>
                ) : showCreditDashboard ? (
                  <>
                    <X size={18} />
                    Hide Dashboard
                  </>
                ) : (
                  <>
                    <BarChart3 size={18} />
                    View Dashboard
                  </>
                )}
              </button>
            </div>

            {showCreditDashboard && creditUsage && typeof creditUsage === 'object' && (
              <div className="space-y-6">
                {/* Totals */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                    <div className="text-xs text-slate-400 uppercase mb-1">Total Credits Used</div>
                    <div className="text-2xl font-bold text-orange-400">{creditUsage?.totals?.totalCreditsUsed || 0}</div>
                  </div>
                  <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                    <div className="text-xs text-slate-400 uppercase mb-1">Total Tool Uses</div>
                    <div className="text-2xl font-bold text-blue-400">{creditUsage?.totals?.totalUsageCount || 0}</div>
                  </div>
                  <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                    <div className="text-xs text-slate-400 uppercase mb-1">Avg Credits/Use</div>
                    <div className="text-2xl font-bold text-purple-400">{creditUsage?.totals?.averageCreditsPerUse || '0'}</div>
                  </div>
                </div>

                {/* Tool Costs Reference */}
                {Array.isArray(creditUsage?.toolCosts) && creditUsage.toolCosts.length > 0 && (
                  <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                    <h4 className="text-sm font-semibold text-white mb-3">Credit Costs per Tool</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                      {(Array.isArray(creditUsage?.toolCosts) ? creditUsage.toolCosts : []).map((tool: any) => (
                        <div key={tool.action} className="flex justify-between items-center p-2 bg-slate-800/50 rounded">
                          <span className="text-slate-300">{tool.toolName}</span>
                          <span className={`font-bold ${
                            tool.cost === 1 ? 'text-emerald-400' :
                            tool.cost === 2 ? 'text-yellow-400' :
                            tool.cost === 5 ? 'text-orange-400' :
                            'text-red-400'
                          }`}>
                            {tool.cost} credit{tool.cost > 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Usage by Tool */}
                {Array.isArray(creditUsage?.usageStats) && creditUsage.usageStats.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-3">Usage by Tool</h4>
                    <div className="space-y-2">
                      {(Array.isArray(creditUsage?.usageStats) ? creditUsage.usageStats : []).map((stat: any, idx: number) => (
                        <div key={idx} className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="font-semibold text-white">{stat?.toolName || 'Unknown Tool'}</div>
                              <div className="text-xs text-slate-400 mt-1">
                                {stat?.usageCount || 0} use{(stat?.usageCount || 0) !== 1 ? 's' : ''} • {stat?.costPerUse || 0} credit{(stat?.costPerUse || 0) > 1 ? 's' : ''} per use
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-orange-400">{stat?.totalCredits || 0} credits</div>
                              <div className="text-xs text-slate-400">total used</div>
                            </div>
                          </div>
                          <div className="w-full bg-slate-800 rounded-full h-2 mt-2">
                            <div
                              className="bg-indigo-500 h-2 rounded-full transition-all"
                              style={{
                                width: `${(creditUsage?.totals?.totalCreditsUsed || 0) > 0 ? (((stat?.totalCredits || 0) / (creditUsage?.totals?.totalCreditsUsed || 1)) * 100) : 0}%`
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!Array.isArray(creditUsage?.usageStats) || !creditUsage?.usageStats || creditUsage.usageStats.length === 0) && (
                  <div className="text-center py-8 text-slate-400">
                    <Activity size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No credit usage data yet. Start using tools to see your usage statistics!</p>
                  </div>
                )}
              </div>
            )}

            {showCreditDashboard && !creditUsage && !loadingCreditUsage && (
              <div className="text-center py-8 text-slate-400">
                <Loader2 size={48} className="mx-auto mb-4 animate-spin opacity-50" />
                <p>Loading credit usage data...</p>
              </div>
            )}
          </div>

          {/* Preferences */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 md:col-span-2">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Globe className="text-blue-400" size={24} />
              Preferences
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                  <Globe size={14} />
                  Preferred Language
                </label>
                {isEditing ? (
                  <select
                    value={editedUser.preferredLanguage || 'English'}
                    onChange={(e) => setEditedUser({ ...editedUser, preferredLanguage: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  >
                    <option value="English">English</option>
                    <option value="French">French</option>
                    <option value="Arabic">Arabic</option>
                    <option value="Spanish">Spanish</option>
                    <option value="German">German</option>
                    <option value="Italian">Italian</option>
                    <option value="Portuguese">Portuguese</option>
                    <option value="Dutch">Dutch</option>
                    <option value="Darija/French">Darija/French</option>
                    <option value="Chinese">Chinese</option>
                    <option value="Japanese">Japanese</option>
                    <option value="Korean">Korean</option>
                  </select>
                ) : (
                  <div className="text-white font-medium">{user.preferredLanguage || 'English'}</div>
                )}
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                  <MapPin size={14} />
                  Target Country
                </label>
                {isEditing ? (
                  <select
                    value={editedUser.preferredCountry || 'United States'}
                    onChange={(e) => setEditedUser({ ...editedUser, preferredCountry: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                  >
                    <option value="Morocco">Morocco</option>
                    <option value="United States">United States</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="France">France</option>
                    <option value="Canada">Canada</option>
                    <option value="Germany">Germany</option>
                    <option value="Spain">Spain</option>
                    <option value="Italy">Italy</option>
                    <option value="Saudi Arabia">Saudi Arabia</option>
                    <option value="UAE">UAE</option>
                    <option value="Egypt">Egypt</option>
                    <option value="Algeria">Algeria</option>
                    <option value="Brazil">Brazil</option>
                    <option value="Mexico">Mexico</option>
                    <option value="Australia">Australia</option>
                    <option value="Japan">Japan</option>
                    <option value="South Korea">South Korea</option>
                    <option value="China">China</option>
                    <option value="India">India</option>
                  </select>
                ) : (
                  <div className="text-white font-medium">{user.preferredCountry || 'United States'}</div>
                )}
              </div>
            </div>
          </div>

          {/* User Instructions */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 md:col-span-2">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              <BookOpen className="text-blue-400" size={24} />
              {t('profile.gettingStartedTitle') || 'Getting Started Guide'}
            </h3>
            <div className="space-y-4">
              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <CheckCircle className="text-emerald-400" size={18} />
                  {t('profile.step1Title') || 'Step 1: Verify Your Email'}
                </h4>
                <p className="text-sm text-slate-300">
                  {user.emailVerified 
                    ? (t('profile.emailVerified') || '✅ Your email is verified. You have access to all features!')
                    : (t('profile.emailNotVerified') || '⚠️ Please verify your email address to receive your 10 free credits and unlock all features. Check your inbox for the verification code.')}
                </p>
              </div>
              
              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <Zap className="text-yellow-400" size={18} />
                  {t('profile.step2Title') || 'Step 2: Understand Credits'}
                </h4>
                <p className="text-sm text-slate-300 mb-2">
                  {t('profile.creditsIntro') || 'Most tools cost'} <strong className="text-white">{t('profile.oneCredit') || '1 credit'}</strong> {t('profile.creditsPerUse') || 'per use. Some tools have different costs:'}
                </p>
                <ul className="text-xs text-slate-400 space-y-1 ml-4 list-disc">
                  <li>{t('profile.creditCost1') || 'SEO Audit, Keyword Research, Competitor Analysis:'} <strong className="text-white">{t('profile.oneCredit') || '1 credit'}</strong></li>
                  <li>{t('profile.creditCost2') || 'Content Optimizer:'} <strong className="text-white">{t('profile.twoCredits') || '2 credits'}</strong></li>
                  <li>{t('profile.creditCost3') || 'Speed Optimizer:'} <strong className="text-white">{t('profile.fiveCredits') || '5 credits'}</strong></li>
                </ul>
                <p className="text-sm text-slate-300 mt-2">
                  {user.plan === 'free' 
                    ? (t('profile.creditsRemaining', { credits: String(user.credits) }) || `You have ${user.credits} credits remaining. Upgrade to a paid plan for unlimited credits!`)
                    : (t('profile.unlimitedCredits') || '✅ You have unlimited credits with your paid plan!')}
                </p>
              </div>
              
              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <Rocket className="text-indigo-400" size={18} />
                  {t('profile.step3Title') || 'Step 3: Start Using Tools'}
                </h4>
                <p className="text-sm text-slate-300 mb-2">
                  {t('profile.toolsIntro') || 'Navigate to the dashboard and choose a tool:'}
                </p>
                <ul className="text-xs text-slate-400 space-y-1 ml-4 list-disc">
                  <li><strong className="text-white">{t('profile.tool1Name') || 'SEO Audit:'}</strong> {t('profile.tool1Desc') || 'Analyze any website for SEO issues'}</li>
                  <li><strong className="text-white">{t('profile.tool2Name') || 'Keyword Research:'}</strong> {t('profile.tool2Desc') || 'Find profitable keywords for your niche'}</li>
                  <li><strong className="text-white">{t('profile.tool3Name') || 'Competitor Analysis:'}</strong> {t('profile.tool3Desc') || 'See what your competitors are doing'}</li>
                  <li><strong className="text-white">{t('profile.tool4Name') || 'AI Tools:'}</strong> {t('profile.tool4Desc') || 'Generate content, meta tags, FAQs, and more'}</li>
                  <li><strong className="text-white">{t('profile.tool5Name') || 'SERP Rank Tracker:'}</strong> {t('profile.tool5Desc') || 'Track your keyword rankings'}</li>
                </ul>
              </div>
              
              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <CreditCard className="text-emerald-400" size={18} />
                  {t('profile.step4Title') || 'Step 4: Upgrade When Ready'}
                </h4>
                <p className="text-sm text-slate-300">
                  {user.plan === 'free' 
                    ? (t('profile.upgradeMessage') || 'When you run out of credits, upgrade to Weekly ($10), Monthly ($29), or Yearly ($290) for unlimited access to all tools!')
                    : (t('profile.paidPlanMessage', { plan: user.plan }) || `You're on the ${user.plan} plan. Enjoy unlimited access to all features!`)}
                </p>
              </div>
            </div>
          </div>

          {/* Consumption History */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 md:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <BarChart3 className="text-purple-400" size={24} />
                My Consumption & History
              </h3>
              <button
                onClick={handleLoadConsumption}
                disabled={loadingConsumption}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors disabled:opacity-50"
              >
                <History size={18} />
                {loadingConsumption ? 'Loading...' : 'View History'}
              </button>
            </div>

            {showConsumption && consumption && (
              <div className="space-y-6">
                {/* Statistics */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-slate-900/50 p-4 rounded-lg">
                    <div className="text-xs text-slate-400 uppercase mb-1">Total Actions</div>
                    <div className="text-2xl font-bold text-white">{consumption.totalActions}</div>
                  </div>
                  <div className="bg-slate-900/50 p-4 rounded-lg">
                    <div className="text-xs text-slate-400 uppercase mb-1">SEO Audits</div>
                    <div className="text-2xl font-bold text-blue-400">{consumption.audits}</div>
                  </div>
                  <div className="bg-slate-900/50 p-4 rounded-lg">
                    <div className="text-xs text-slate-400 uppercase mb-1">Keyword Searches</div>
                    <div className="text-2xl font-bold text-purple-400">{consumption.keywordSearches}</div>
                  </div>
                  <div className="bg-slate-900/50 p-4 rounded-lg">
                    <div className="text-xs text-slate-400 uppercase mb-1">Competitor Analyses</div>
                    <div className="text-2xl font-bold text-green-400">{consumption.competitorAnalyses}</div>
                  </div>
                  <div className="bg-slate-900/50 p-4 rounded-lg">
                    <div className="text-xs text-slate-400 uppercase mb-1">Credits Used</div>
                    <div className="text-2xl font-bold text-orange-400">{consumption.creditsDeducted}</div>
                  </div>
                  <div className="bg-slate-900/50 p-4 rounded-lg">
                    <div className="text-xs text-slate-400 uppercase mb-1">Logins</div>
                    <div className="text-2xl font-bold text-indigo-400">{consumption.logins}</div>
                  </div>
                </div>

                {consumption.lastActivity && (
                  <div className="text-sm text-slate-400">
                    Last activity: {new Date(consumption.lastActivity).toLocaleString()}
                  </div>
                )}

                {/* Activity History */}
                {activity.length > 0 && (
                  <div>
                    <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                      <Activity size={20} />
                      Recent Activity
                    </h4>
                    <div className="bg-slate-900/50 rounded-lg overflow-hidden">
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-800 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left">Action</th>
                              <th className="px-4 py-2 text-left">Resource</th>
                              <th className="px-4 py-2 text-left">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-700">
                            {activity.map((act: any, idx: number) => (
                              <tr key={idx} className="hover:bg-slate-700/50">
                                <td className="px-4 py-2">
                                  <span className="px-2 py-1 rounded bg-indigo-500/20 text-indigo-400 text-xs">
                                    {act.action}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-slate-300">{act.resource || 'N/A'}</td>
                                <td className="px-4 py-2 text-slate-400">
                                  {act.createdAt ? formatDateTime(act.createdAt) : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {showConsumption && !consumption && !loadingConsumption && (
              <div className="text-center py-8 text-slate-400">
                No consumption data available yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

